import clsx from 'clsx';
import { memo, useId, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import type { BannerProps } from './types';

import { getStatusIcon } from '../../utils/getStatusIcon';
import { getStatusLiveRole } from '../../utils/getStatusLiveRole';
import { Button } from '../Button/Button';
import { IconButton } from '../Button/subcomponents/IconButton';
import { Icon } from '../Icon/Icon';
import { Layout } from '../Layout/Layout';
import { Link } from '../Link/Link';
import { Text } from '../Text/Text';
import './Banner.css';

const messages = defineMessages({
  collapse: {
    id: 'Banner.collapse',
    defaultMessage: 'Hide details',
    description: 'Accessible name for the button that collapses a banner',
  },
  dismiss: {
    id: 'Banner.dismiss',
    defaultMessage: 'Dismiss',
    description: 'Accessible name for the banner dismiss button',
  },
  expand: {
    id: 'Banner.expand',
    defaultMessage: 'Show details',
    description: 'Accessible name for the button that expands a banner',
  },
  statusCautious: {
    id: 'Banner.statusCautious',
    defaultMessage: 'Caution',
    description: 'Accessible name for a cautious banner status icon',
  },
  statusCritical: {
    id: 'Banner.statusCritical',
    defaultMessage: 'Critical',
    description: 'Accessible name for a critical banner status icon',
  },
  statusInformational: {
    id: 'Banner.statusInformational',
    defaultMessage: 'Information',
    description: 'Accessible name for an informational banner status icon',
  },
  statusPositive: {
    id: 'Banner.statusPositive',
    defaultMessage: 'Success',
    description: 'Accessible name for a positive banner status icon',
  },
});

const statusIconLabels = {
  cautious: messages.statusCautious,
  critical: messages.statusCritical,
  informational: messages.statusInformational,
  positive: messages.statusPositive,
} as const;

export const Banner = memo(
  ({
    text,
    action,
    collapsible = false,
    items,
    link,
    status = 'informational',
    title,
    onDismiss,
    onToggle,
  }: BannerProps) => {
    const intl = useIntl();
    const detailsId = useId();
    const [expanded, setExpanded] = useState(false);
    const dismissLabel = intl.formatMessage(messages.dismiss);
    const statusIcon = getStatusIcon(status);
    const showDetails = !collapsible || expanded;

    const handleToggle = () => {
      const next = !expanded;
      setExpanded(next);
      onToggle?.(next);
    };

    return (
      <div
        className={clsx('eds-banner', `eds-banner--status-${status}`, {
          'eds-banner--actionable': Boolean(action),
          'eds-banner--collapsible': collapsible,
          'eds-banner--dismissible': Boolean(onDismiss),
        })}
        role={getStatusLiveRole(status)}
      >
        <span className="eds-banner--status-icon">
          <Icon
            icon={statusIcon}
            label={intl.formatMessage(statusIconLabels[status])}
            size="m"
          />
        </span>
        <Layout align="start" className="eds-banner--body" spacing="s" wrap>
          <Layout
            className="eds-banner--content"
            direction="column"
            flex="auto"
            spacing="s"
          >
            <div className="eds-banner--message-block">
              {title ? (
                <Text as="p" variant="body-semi-m">
                  {title}
                </Text>
              ) : null}
              {showDetails ? (
                <div className="eds-banner--details" id={detailsId}>
                  <Text as="p" variant="body-m">
                    {text}
                  </Text>
                  {items?.length ? (
                    <ul className="eds-banner--items">
                      {items.map((item, index) => (
                        <li
                          className="eds-banner--item"
                          key={`${index}-${item}`}
                        >
                          <Text as="span" variant="body-m">
                            {item}
                          </Text>
                        </li>
                      ))}
                    </ul>
                  ) : null}
                </div>
              ) : null}
            </div>
            {link ? (
              <Link
                external={link.external}
                href={link.href}
                size={link.size ?? 'm'}
                text={link.text}
              />
            ) : null}
          </Layout>
          {action ? (
            <span className="eds-banner--action">
              <Button
                disabled={action.disabled}
                isLoading={action.isLoading}
                label={action.label}
                onClick={action.onClick}
                size="s"
                text={action.text}
                tooltip={action.tooltip}
                variant={action.variant ?? 'secondary'}
              />
            </span>
          ) : null}
        </Layout>
        {collapsible || onDismiss ? (
          <Layout align="center" className="eds-banner--controls" spacing="xs">
            {collapsible ? (
              <span
                className={clsx('eds-banner--toggle', {
                  'eds-banner--toggle--open': expanded,
                })}
              >
                <IconButton
                  aria-controls={expanded ? detailsId : undefined}
                  aria-expanded={expanded}
                  icon={{ key: 'chevron-down' }}
                  label={intl.formatMessage(
                    expanded ? messages.collapse : messages.expand,
                  )}
                  size="s"
                  variant="tertiary"
                  onClick={handleToggle}
                />
              </span>
            ) : null}
            {onDismiss ? (
              <span className="eds-banner--dismiss">
                <IconButton
                  icon={{ key: 'x', label: dismissLabel }}
                  label={dismissLabel}
                  onClick={onDismiss}
                  size="s"
                  variant="tertiary"
                />
              </span>
            ) : null}
          </Layout>
        ) : null}
      </div>
    );
  },
);

Banner.displayName = 'Banner';
